import { GridFilterModel } from '@mui/x-data-grid-pro';
import { ExtendedDriver } from '@hooks/useDriverTractors.types';
import { isTractorSelectable } from '../Tractor/types';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';

export interface DriverToolbarFilters {
	unassigned: boolean;
	onRouteWithNoJobs: boolean;
	hazmatEndorsed: boolean;
	filterModel: GridFilterModel;
}

interface DriverSelectionStatus {
	/** Flag indicating that the Driver is in adequate status to be selected */
	isSelectable: boolean;
	/** If the Driver is unable to be selected, this contains each reason why */
	disabledReason: string[];
}

export function isDriverSelectable(
	row: ExtendedDriver,
	/** The Leg the Driver is being selected for, required for validation */
	requiresHazmatCert: boolean,
	/** Flag indicating default truck validation should be applied to determine this driver row as selectable */
	applyDefaultTractorValidation?: boolean
): DriverSelectionStatus {
	let isValidForSelection = true;
	const disabledReasons: string[] = [];

	const today = dayjsNow().valueOf();

	if (!row.cdlExpirationDate || row.cdlExpirationDate.unix < today) {
		isValidForSelection = false;
		disabledReasons.push('Driver CDL License is missing or expired.');
	}

	/** Driver needs valid medical to be selected */
	if (!row.medExpirationDate || row.medExpirationDate.unix < today) {
		isValidForSelection = false;
		disabledReasons.push('Medical Insurance is missing or expired.');
	}

	if (requiresHazmatCert && (!row.hazmatExpirationDate || row.hazmatExpirationDate.unix < today)) {
		isValidForSelection = false;
		disabledReasons.push('Hazmat Certified Driver is required.');
	}

	/** When validating the Driver, if he only has one Truck available AND that is his default Truck
	 * We want to prevent selecting this Driver and add the validation warning now.
	 */
	if (applyDefaultTractorValidation) {
		const defaultTruck = row.tractors.find((t) => t.id === row.tractorId);
		if (row.tractors.length === 0) {
			isValidForSelection = false;
			disabledReasons.push('Driver has no available Trucks.');
		} else if (row.tractors.length === 1 && defaultTruck) {
			const tractorValidation = isTractorSelectable(
				defaultTruck,
				// intentionally omitting requiresHazmatCert value to avoid duplicating check that occurs above
				undefined
			);
			if (!tractorValidation.isSelectable) {
				isValidForSelection = false;
				disabledReasons.push(...tractorValidation.disabledReason);
			}
		}
	}

	return {
		isSelectable: isValidForSelection,
		disabledReason: disabledReasons
	};
}
